/**
 * Adapted from PlayCanvas Engine's scripts/esm/gsplat/gsplat-mesh.mjs at
 * d88289fb0c17be76ec4159974faf0d07d3582a6b (MIT License).
 */

import * as THREE from 'three'
import type { GSplatResourceLease } from '@/plugins/gsplat/GSplatHandleBinding'
import type { GSplatGraphicsAdapter } from '@/plugins/gsplat/graphics/GSplatGraphicsAdapter'
import { GSplatFormat } from '@/plugins/gsplat/lod/GSplatFormat'
import type {
  GaussianSplatHandle,
  GaussianSplatLoadRequest,
} from '@/plugins/gsplat/types/PluginTypes'
import { GSplatContainer } from '@/plugins/gsplat/unified/resource/GSplatContainer'
import { getLoggerManager } from '@/shared/logging'
import type { AssetId, Disposable } from '@/shared/types/core'

const log = getLoggerManager().getLogger('gsplat:mesh')
const DEGENERATE_THRESHOLD = 0.0001

export interface GSplatMeshColor {
  readonly r: number
  readonly g: number
  readonly b: number
  readonly a: number
}

export interface GSplatMeshSplat extends GSplatMeshColor {
  readonly x: number
  readonly y: number
  readonly z: number
}

export interface GSplatMeshRuntime {
  readonly graphicsAdapter: GSplatGraphicsAdapter
  createWithResource(
    request: Readonly<GaussianSplatLoadRequest>,
    resource: GSplatResourceLease
  ): GaussianSplatHandle
  replaceWithResource(
    id: AssetId,
    resource: GSplatResourceLease,
    metadata?: { readonly url?: string; readonly name?: string }
  ): GaussianSplatHandle
  mount(handle: GaussianSplatHandle): void
  disposeAsset(id: AssetId): void
}

export interface GSplatMeshOptions {
  readonly id?: AssetId
  readonly name?: string
}

export interface GSplatMeshBuildOptions {
  readonly splatSize?: number
  readonly recursive?: boolean
  readonly margin?: number
}

export type GSplatMeshErrorCode =
  | 'GSPLAT_MESH_DISPOSED'
  | 'GSPLAT_MESH_INVALID_SOURCE'
  | 'GSPLAT_MESH_INVALID_SPLAT_SIZE'
  | 'GSPLAT_MESH_INVALID_MARGIN'
  | 'GSPLAT_MESH_INVALID_MATRIX'
  | 'GSPLAT_MESH_INVALID_POSITION'
  | 'GSPLAT_MESH_INVALID_INDEX'
  | 'GSPLAT_MESH_INVALID_COLOR'
  | 'GSPLAT_MESH_CAPACITY_OVERFLOW'
  | 'GSPLAT_MESH_ALLOCATION_FAILED'
  | 'GSPLAT_MESH_FILL_FAILED'
  | 'GSPLAT_MESH_UPLOAD_FAILED'
  | 'GSPLAT_MESH_UPDATE_FAILED'
  | 'GSPLAT_MESH_COMMIT_FAILED'
  | 'GSPLAT_MESH_STALE_OPERATION'

export class GSplatMeshError extends Error {
  constructor(
    readonly code: GSplatMeshErrorCode,
    message: string,
    readonly cause?: unknown
  ) {
    super(message)
    this.name = 'GSplatMeshError'
  }
}

interface RasterScratch {
  readonly edge1: THREE.Vector3
  readonly edge2: THREE.Vector3
  readonly normal: THREE.Vector3
  readonly edgeDirection: THREE.Vector3
  readonly perpendicularDirection: THREE.Vector3
}

interface GSplatMeshSnapshot {
  readonly positions: Float64Array
  readonly indices: Uint32Array
  readonly transform: THREE.Matrix4
  readonly color: GSplatMeshColor
}

function createRasterScratch(): RasterScratch {
  return {
    edge1: new THREE.Vector3(),
    edge2: new THREE.Vector3(),
    normal: new THREE.Vector3(),
    edgeDirection: new THREE.Vector3(),
    perpendicularDirection: new THREE.Vector3(),
  }
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error)
}

function isFiniteMatrix(matrix: THREE.Matrix4): boolean {
  return matrix.elements.every(Number.isFinite)
}

/**
 * 保持上游 current-first / children-order DFS，只把 RenderComponent 适配为 Three.js Mesh。
 */
export function collectRenderComponents(
  object: THREE.Object3D,
  recursive: boolean,
  results: THREE.Mesh[]
): void {
  if (object instanceof THREE.Mesh) results.push(object)
  if (!recursive) return
  for (let index = 0; index < object.children.length; index++) {
    collectRenderComponents(object.children[index], true, results)
  }
}

/** 保持上游 StandardMaterial 的 emissive/diffuse/transparent alpha 规则。 */
export function getMaterialColor(
  material: THREE.Material | readonly THREE.Material[] | null | undefined
): GSplatMeshColor {
  let r = 1
  let g = 1
  let b = 1
  let a = 1

  if (material instanceof THREE.MeshStandardMaterial) {
    const emissive = material.emissive
    if (emissive && (emissive.r > 0.001 || emissive.g > 0.001 || emissive.b > 0.001)) {
      r = emissive.r
      g = emissive.g
      b = emissive.b
    } else {
      const diffuse = material.color
      if (diffuse) {
        r = diffuse.r
        g = diffuse.g
        b = diffuse.b
      }
    }
    a = material.transparent ? (material.opacity ?? 1) : 1
    if (a < 1) a *= 0.5
  }

  return Object.freeze({ r, g, b, a })
}

/**
 * 冻结 PlayCanvas v0→v1 基边、margin 与 scanline/point 均匀覆盖算法。
 */
export function rasterizeTriangle(
  v0: THREE.Vector3,
  v1: THREE.Vector3,
  v2: THREE.Vector3,
  splatSize: number,
  marginFactor: number,
  color: GSplatMeshColor,
  outSplats: GSplatMeshSplat[],
  scratch: RasterScratch = createRasterScratch()
): void {
  const { edge1, edge2, normal, edgeDirection, perpendicularDirection } = scratch
  edge1.subVectors(v1, v0)
  const baseLength = edge1.length()
  if (baseLength < DEGENERATE_THRESHOLD) return

  edgeDirection.copy(edge1).normalize()
  edge2.subVectors(v2, v0)
  normal.crossVectors(edge1, edge2)
  const doubleArea = normal.length()
  if (doubleArea < DEGENERATE_THRESHOLD) return
  normal.normalize()
  perpendicularDirection.crossVectors(normal, edgeDirection)

  const apexX = edge2.dot(edgeDirection)
  const apexY = edge2.dot(perpendicularDirection)
  const height = Math.abs(apexY)
  if (height < DEGENERATE_THRESHOLD) return

  const length02 = Math.sqrt(apexX * apexX + apexY * apexY)
  const length12 = Math.sqrt((apexX - baseLength) * (apexX - baseLength) + apexY * apexY)
  const spacing = splatSize * 1.5
  const margin = splatSize * marginFactor
  const marginOffset02 =
    length02 > DEGENERATE_THRESHOLD ? (margin * length02) / Math.abs(apexY) : margin
  const marginOffset12 =
    length12 > DEGENERATE_THRESHOLD ? (margin * length12) / Math.abs(apexY) : margin
  const ySign = apexY >= 0 ? 1 : -1
  const startScanY = margin
  const endScanY = height - margin
  if (startScanY >= endScanY) return

  const scanRange = endScanY - startScanY
  const numScanlines = Math.max(1, Math.floor(scanRange / spacing) + 1)
  const scanStep = numScanlines > 1 ? scanRange / (numScanlines - 1) : 0

  for (let scanIndex = 0; scanIndex < numScanlines; scanIndex++) {
    const scanDistance =
      numScanlines > 1 ? startScanY + scanIndex * scanStep : (startScanY + endScanY) * 0.5
    const localY = scanDistance * ySign
    const t = scanDistance / height
    const intersection02 = t * apexX
    const intersection12 = baseLength + t * (apexX - baseLength)
    const leftX = Math.min(intersection02, intersection12)
    const rightX = Math.max(intersection02, intersection12)
    const leftMargin = intersection02 < intersection12 ? marginOffset02 : marginOffset12
    const rightMargin = intersection02 < intersection12 ? marginOffset12 : marginOffset02
    const scanStartX = leftX + leftMargin
    const scanEndX = rightX - rightMargin
    if (scanStartX >= scanEndX) continue

    const scanLength = scanEndX - scanStartX
    const numPoints = Math.max(1, Math.floor(scanLength / spacing) + 1)
    if (numPoints === 1) {
      const px = (scanStartX + scanEndX) * 0.5
      outSplats.push({
        x: v0.x + px * edgeDirection.x + localY * perpendicularDirection.x,
        y: v0.y + px * edgeDirection.y + localY * perpendicularDirection.y,
        z: v0.z + px * edgeDirection.z + localY * perpendicularDirection.z,
        r: color.r,
        g: color.g,
        b: color.b,
        a: color.a,
      })
      continue
    }

    const pointStep = scanLength / (numPoints - 1)
    for (let pointIndex = 0; pointIndex < numPoints; pointIndex++) {
      const px = scanStartX + pointIndex * pointStep
      outSplats.push({
        x: v0.x + px * edgeDirection.x + localY * perpendicularDirection.x,
        y: v0.y + px * edgeDirection.y + localY * perpendicularDirection.y,
        z: v0.z + px * edgeDirection.z + localY * perpendicularDirection.z,
        r: color.r,
        g: color.g,
        b: color.b,
        a: color.a,
      })
    }
  }
}

/** 显式 build、由 GSplatSystem 拥有生命周期的 backend-neutral mesh surface generator。 */
export class GSplatMesh implements Disposable {
  private readonly id?: AssetId
  private readonly name: string
  private formatValue: GSplatFormat | null = null
  private handleValue: GaussianSplatHandle | null = null
  private stagedHandle: GaussianSplatHandle | null = null
  private stagedContainer: GSplatContainer | null = null
  private committedSplatCount = 0
  private generation = 0
  private initialized = false
  private disposed = false
  private errorValue: GSplatMeshError | null = null

  constructor(
    private readonly runtime: GSplatMeshRuntime,
    options: GSplatMeshOptions = {},
    private readonly onDispose?: (generator: GSplatMesh) => void
  ) {
    this.id = options.id
    this.name = options.name ?? 'GSplatMesh'
  }

  get handle(): GaussianSplatHandle | null {
    return this.handleValue
  }

  get format(): GSplatFormat | null {
    return this.formatValue
  }

  get lastError(): GSplatMeshError | null {
    return this.errorValue
  }

  get splatCount(): number {
    return this.committedSplatCount
  }

  initialize(): void {
    this.assertActive()
    if (this.initialized) return
    this.initialized = true
  }

  buildFromEntity(
    source: THREE.Object3D,
    options: GSplatMeshBuildOptions = {}
  ): GaussianSplatHandle | null {
    this.assertActive()
    if (!this.initialized) this.initialize()
    const operationGeneration = ++this.generation
    try {
      const splatSize = options.splatSize ?? 0.01
      const recursive = options.recursive ?? true
      const margin = options.margin ?? 0.65
      this.validateOptions(source, splatSize, margin)
      const snapshots = this.snapshotHierarchy(source, recursive)
      const splats = this.rasterizeSnapshots(snapshots, splatSize, margin)
      this.assertGeneration(operationGeneration)
      if (splats.length === 0) {
        this.commitEmpty(operationGeneration)
      } else {
        this.buildContainer(splats, splatSize, operationGeneration)
      }
      this.assertGeneration(operationGeneration)
      this.errorValue = null
      return this.handleValue
    } catch (error) {
      const structured = this.toError(error, 'GSPLAT_MESH_COMMIT_FAILED', 'mesh 构建失败')
      if (this.generation === operationGeneration) this.errorValue = structured
      log.warn(`GSplatMesh 构建失败: code=${structured.code}, reason=${structured.message}`)
      throw structured
    }
  }

  clear(): void {
    this.assertActive()
    const operationGeneration = ++this.generation
    if (this.committedSplatCount === 0) {
      this.errorValue = null
      return
    }
    this._destroyContainer(operationGeneration)
    this.errorValue = null
  }

  destroy(): void {
    if (this.disposed) return
    this.disposed = true
    this.generation++
    let firstError: unknown
    try {
      this.stagedContainer?.destroy()
    } catch (error) {
      firstError = error
    }
    this.stagedContainer = null
    this.committedSplatCount = 0
    const handle = this.handleValue
    const stagedHandle = this.stagedHandle
    this.handleValue = null
    this.stagedHandle = null
    const disposeHandle = (target: GaussianSplatHandle | null) => {
      if (!target) return
      try {
        this.runtime.disposeAsset(target.id)
      } catch (error) {
        firstError ??= error
      }
    }
    disposeHandle(handle)
    if (stagedHandle !== handle) disposeHandle(stagedHandle)
    try {
      this.onDispose?.(this)
    } catch (error) {
      firstError ??= error
    }
    if (firstError) throw firstError
  }

  dispose(): void {
    this.destroy()
  }

  private validateOptions(source: THREE.Object3D, splatSize: number, margin: number): void {
    if (!(source instanceof THREE.Object3D)) {
      throw new GSplatMeshError('GSPLAT_MESH_INVALID_SOURCE', 'GSplatMesh source 必须是 Object3D')
    }
    if (!Number.isFinite(splatSize) || splatSize <= 0) {
      throw new GSplatMeshError(
        'GSPLAT_MESH_INVALID_SPLAT_SIZE',
        `GSplatMesh splatSize 必须是有限正数: ${splatSize}`
      )
    }
    if (!Number.isFinite(margin) || margin < 0) {
      throw new GSplatMeshError(
        'GSPLAT_MESH_INVALID_MARGIN',
        `GSplatMesh margin 必须是有限非负数: ${margin}`
      )
    }
  }

  private snapshotHierarchy(
    source: THREE.Object3D,
    recursive: boolean
  ): readonly GSplatMeshSnapshot[] {
    source.updateWorldMatrix(true, true)
    if (!isFiniteMatrix(source.matrixWorld)) {
      throw new GSplatMeshError('GSPLAT_MESH_INVALID_MATRIX', 'GSplatMesh root world matrix 非有限')
    }
    const determinant = source.matrixWorld.determinant()
    if (!Number.isFinite(determinant) || determinant === 0) {
      throw new GSplatMeshError('GSPLAT_MESH_INVALID_MATRIX', 'GSplatMesh root world matrix 不可逆')
    }
    const inverseRoot = source.matrixWorld.clone().invert()
    if (!isFiniteMatrix(inverseRoot)) {
      throw new GSplatMeshError(
        'GSPLAT_MESH_INVALID_MATRIX',
        'GSplatMesh root inverse matrix 非有限'
      )
    }

    const meshes: THREE.Mesh[] = []
    collectRenderComponents(source, recursive, meshes)
    const snapshots: GSplatMeshSnapshot[] = []
    for (const mesh of meshes) {
      const geometry = mesh.geometry
      const position = geometry?.getAttribute('position')
      if (!position || position.count === 0) continue
      const positions = new Float64Array(position.count * 3)
      for (let index = 0; index < position.count; index++) {
        const offset = index * 3
        positions[offset] = position.getX(index)
        positions[offset + 1] = position.getY(index)
        positions[offset + 2] = position.getZ(index)
        if (
          !Number.isFinite(positions[offset]) ||
          !Number.isFinite(positions[offset + 1]) ||
          !Number.isFinite(positions[offset + 2])
        ) {
          throw new GSplatMeshError(
            'GSPLAT_MESH_INVALID_POSITION',
            `GSplatMesh position[${index}] 必须是有限三维坐标`
          )
        }
      }

      const sourceIndex = geometry.getIndex()
      const indexCount = sourceIndex?.count ?? position.count
      if (indexCount < 3) continue
      if (indexCount % 3 !== 0) {
        throw new GSplatMeshError(
          'GSPLAT_MESH_INVALID_INDEX',
          `GSplatMesh index count 必须是三的倍数: ${indexCount}`
        )
      }
      const indices = new Uint32Array(indexCount)
      for (let index = 0; index < indexCount; index++) {
        const vertexIndex = sourceIndex ? sourceIndex.getX(index) : index
        if (
          !Number.isSafeInteger(vertexIndex) ||
          vertexIndex < 0 ||
          vertexIndex >= position.count
        ) {
          throw new GSplatMeshError(
            'GSPLAT_MESH_INVALID_INDEX',
            `GSplatMesh index[${index}] 越界: ${vertexIndex}/${position.count}`
          )
        }
        indices[index] = vertexIndex
      }

      if (!isFiniteMatrix(mesh.matrixWorld)) {
        throw new GSplatMeshError(
          'GSPLAT_MESH_INVALID_MATRIX',
          'GSplatMesh mesh world matrix 非有限'
        )
      }
      const transform = new THREE.Matrix4().multiplyMatrices(inverseRoot, mesh.matrixWorld)
      if (!isFiniteMatrix(transform)) {
        throw new GSplatMeshError('GSPLAT_MESH_INVALID_MATRIX', 'GSplatMesh relative matrix 非有限')
      }
      const color = getMaterialColor(mesh.material)
      if (![color.r, color.g, color.b, color.a].every(Number.isFinite)) {
        throw new GSplatMeshError(
          'GSPLAT_MESH_INVALID_COLOR',
          'GSplatMesh material color 必须是有限 RGBA'
        )
      }
      snapshots.push(Object.freeze({ positions, indices, transform, color }))
    }
    return Object.freeze(snapshots)
  }

  private rasterizeSnapshots(
    snapshots: readonly GSplatMeshSnapshot[],
    splatSize: number,
    margin: number
  ): GSplatMeshSplat[] {
    const splats: GSplatMeshSplat[] = []
    const scratch = createRasterScratch()
    const v0 = new THREE.Vector3()
    const v1 = new THREE.Vector3()
    const v2 = new THREE.Vector3()
    for (const snapshot of snapshots) {
      for (let index = 0; index < snapshot.indices.length; index += 3) {
        const index0 = snapshot.indices[index] * 3
        const index1 = snapshot.indices[index + 1] * 3
        const index2 = snapshot.indices[index + 2] * 3
        v0.set(
          snapshot.positions[index0],
          snapshot.positions[index0 + 1],
          snapshot.positions[index0 + 2]
        )
        v1.set(
          snapshot.positions[index1],
          snapshot.positions[index1 + 1],
          snapshot.positions[index1 + 2]
        )
        v2.set(
          snapshot.positions[index2],
          snapshot.positions[index2 + 1],
          snapshot.positions[index2 + 2]
        )
        v0.applyMatrix4(snapshot.transform)
        v1.applyMatrix4(snapshot.transform)
        v2.applyMatrix4(snapshot.transform)
        if (![v0.x, v0.y, v0.z, v1.x, v1.y, v1.z, v2.x, v2.y, v2.z].every(Number.isFinite)) {
          throw new GSplatMeshError(
            'GSPLAT_MESH_INVALID_POSITION',
            'GSplatMesh transformed position 必须是有限三维坐标'
          )
        }
        rasterizeTriangle(v0, v1, v2, splatSize, margin, snapshot.color, splats, scratch)
        if (!Number.isSafeInteger(splats.length)) {
          throw new GSplatMeshError(
            'GSPLAT_MESH_CAPACITY_OVERFLOW',
            'GSplatMesh splat 数量超出安全整数范围'
          )
        }
      }
    }
    return splats
  }

  private buildContainer(
    splats: readonly GSplatMeshSplat[],
    splatSize: number,
    operationGeneration: number
  ): void {
    const adapter = this.runtime.graphicsAdapter
    let container: GSplatContainer | null = null
    try {
      try {
        const format = (this.formatValue ??= GSplatFormat.createSimpleFormat(adapter))
        container = new GSplatContainer(adapter, splats.length, format)
      } catch (error) {
        throw new GSplatMeshError(
          'GSPLAT_MESH_ALLOCATION_FAILED',
          `GSplatMesh container 分配失败: ${errorMessage(error)}`,
          error
        )
      }
      this.stagedContainer = container
      this.assertGeneration(operationGeneration)

      const texelCount = container.textureDimensions.width * container.textureDimensions.height
      const centerData = new Float32Array(texelCount * 4)
      const colorData = new Uint16Array(texelCount * 4)
      const centers = container.centers
      let minX = Number.POSITIVE_INFINITY
      let minY = Number.POSITIVE_INFINITY
      let minZ = Number.POSITIVE_INFINITY
      let maxX = Number.NEGATIVE_INFINITY
      let maxY = Number.NEGATIVE_INFINITY
      let maxZ = Number.NEGATIVE_INFINITY
      try {
        for (let index = 0; index < splats.length; index++) {
          const splat = splats[index]
          const centerOffset = index * 4
          centerData[centerOffset] = splat.x
          centerData[centerOffset + 1] = splat.y
          centerData[centerOffset + 2] = splat.z
          centerData[centerOffset + 3] = splatSize
          colorData[centerOffset] = THREE.DataUtils.toHalfFloat(splat.r)
          colorData[centerOffset + 1] = THREE.DataUtils.toHalfFloat(splat.g)
          colorData[centerOffset + 2] = THREE.DataUtils.toHalfFloat(splat.b)
          colorData[centerOffset + 3] = THREE.DataUtils.toHalfFloat(splat.a)
          const sortingOffset = index * 3
          centers[sortingOffset] = splat.x
          centers[sortingOffset + 1] = splat.y
          centers[sortingOffset + 2] = splat.z
          minX = Math.min(minX, splat.x)
          minY = Math.min(minY, splat.y)
          minZ = Math.min(minZ, splat.z)
          maxX = Math.max(maxX, splat.x)
          maxY = Math.max(maxY, splat.y)
          maxZ = Math.max(maxZ, splat.z)
        }
      } catch (error) {
        throw new GSplatMeshError(
          'GSPLAT_MESH_FILL_FAILED',
          `GSplatMesh stream 填充失败: ${errorMessage(error)}`,
          error
        )
      }

      const centerTexture = container.getTexture('dataCenter')
      const colorTexture = container.getTexture('dataColor')
      if (!centerTexture || !colorTexture) {
        throw new GSplatMeshError(
          'GSPLAT_MESH_ALLOCATION_FAILED',
          'GSplatMesh simple format texture 不完整'
        )
      }
      try {
        adapter.updateTexture(centerTexture, centerData)
        adapter.updateTexture(colorTexture, colorData)
      } catch (error) {
        throw new GSplatMeshError(
          'GSPLAT_MESH_UPLOAD_FAILED',
          `GSplatMesh texture 上传失败: ${errorMessage(error)}`,
          error
        )
      }
      this.assertGeneration(operationGeneration)

      try {
        container.aabb.set(
          new THREE.Vector3(minX - splatSize, minY - splatSize, minZ - splatSize),
          new THREE.Vector3(maxX + splatSize, maxY + splatSize, maxZ + splatSize)
        )
        container.update(splats.length)
      } catch (error) {
        throw new GSplatMeshError(
          'GSPLAT_MESH_UPDATE_FAILED',
          `GSplatMesh container update 失败: ${errorMessage(error)}`,
          error
        )
      }
      this.assertGeneration(operationGeneration)

      const lease = container.acquire()
      container.destroy()
      container = null
      this.stagedContainer = null
      this.commitResource(operationGeneration, lease, splats.length)
    } finally {
      if (this.stagedContainer === container) this.stagedContainer = null
      container?.destroy()
    }
  }

  private commitEmpty(operationGeneration: number): void {
    this.assertGeneration(operationGeneration)
    if (!this.handleValue) {
      this.committedSplatCount = 0
      return
    }
    this._destroyContainer(operationGeneration)
  }

  private _destroyContainer(operationGeneration: number): void {
    this.assertGeneration(operationGeneration)
    if (!this.handleValue) {
      this.committedSplatCount = 0
      return
    }
    const adapter = this.runtime.graphicsAdapter
    let container: GSplatContainer | null = null
    try {
      try {
        const format = (this.formatValue ??= GSplatFormat.createSimpleFormat(adapter))
        container = new GSplatContainer(adapter, 0, format)
      } catch (error) {
        throw new GSplatMeshError(
          'GSPLAT_MESH_ALLOCATION_FAILED',
          `GSplatMesh empty container 分配失败: ${errorMessage(error)}`,
          error
        )
      }
      this.stagedContainer = container
      this.assertGeneration(operationGeneration)
      try {
        container.aabb.makeEmpty()
        container.update(0)
      } catch (error) {
        throw new GSplatMeshError(
          'GSPLAT_MESH_UPDATE_FAILED',
          `GSplatMesh empty container update 失败: ${errorMessage(error)}`,
          error
        )
      }
      this.assertGeneration(operationGeneration)
      const lease = container.acquire()
      container.destroy()
      container = null
      this.stagedContainer = null
      this.commitResource(operationGeneration, lease, 0)
    } finally {
      if (this.stagedContainer === container) this.stagedContainer = null
      container?.destroy()
    }
  }

  private commitResource(
    operationGeneration: number,
    resource: GSplatResourceLease,
    splatCount: number
  ): void {
    let lease: GSplatResourceLease | null = resource
    try {
      this.assertGeneration(operationGeneration)
      let handle: GaussianSplatHandle
      if (this.handleValue) {
        handle = this.runtime.replaceWithResource(this.handleValue.id, lease, { name: this.name })
        lease = null
      } else {
        handle = this.runtime.createWithResource(
          Object.freeze({
            url: `procedural://gsplat-mesh/${this.id ?? 'generated'}`,
            name: this.name,
            ...(this.id ? { id: this.id } : {}),
          }),
          lease
        )
        lease = null
        this.stagedHandle = handle
        try {
          this.runtime.mount(handle)
        } catch (error) {
          try {
            this.runtime.disposeAsset(handle.id)
          } catch (cleanupError) {
            log.warn(`GSplatMesh mount 回滚失败: ${errorMessage(cleanupError)}`)
          } finally {
            this.stagedHandle = null
          }
          throw error
        }
      }
      this.assertGeneration(operationGeneration)
      this.handleValue = handle
      this.stagedHandle = null
      this.committedSplatCount = splatCount
    } catch (error) {
      lease?.dispose()
      const stagedHandle = this.stagedHandle
      this.stagedHandle = null
      if (stagedHandle && stagedHandle !== this.handleValue) {
        try {
          this.runtime.disposeAsset(stagedHandle.id)
        } catch (cleanupError) {
          log.warn(`GSplatMesh staged Handle 回滚失败: ${errorMessage(cleanupError)}`)
        }
      }
      throw this.toError(error, 'GSPLAT_MESH_COMMIT_FAILED', '提交 GSplat Resource 失败')
    }
  }

  private assertGeneration(operationGeneration: number): void {
    if (this.disposed || this.generation !== operationGeneration) {
      throw new GSplatMeshError('GSPLAT_MESH_STALE_OPERATION', 'GSplatMesh build operation 已失效')
    }
  }

  private assertActive(): void {
    if (this.disposed) {
      throw new GSplatMeshError('GSPLAT_MESH_DISPOSED', 'GSplatMesh 已销毁')
    }
  }

  private toError(error: unknown, code: GSplatMeshErrorCode, prefix: string): GSplatMeshError {
    if (error instanceof GSplatMeshError) return error
    return new GSplatMeshError(code, `${prefix}: ${errorMessage(error)}`, error)
  }
}
